import { RoomEvent, Track } from 'livekit-client'
import type { LocalTrackPublication, Participant, Room, RemoteTrackPublication, TrackPublication } from 'livekit-client'

import { CAMERA_STATE_TOPIC, decodeCameraWaiting } from './cameraWaitingSignal'

/**
 * Live mic/camera state for LiveKit participants, exposed OUTSIDE the React
 * `RoomContext`.
 *
 * Most of the app reads LiveKit state through the React context
 * (`useRoomContext`, `useParticipants`, …). The in-game nameplates, however,
 * render inside the `phaser-react-ui` overlay, which mounts in its OWN React
 * root (`Interface` → `createRoot`) and therefore cannot see the app-tree
 * `RoomContext`. Previously their mic/camera icon read a Redux snapshot that a
 * controller effect only refreshed when the *set of participants* changed — so
 * a plain mute/unmute (which doesn't change that set) left the avatar icon
 * stale while the tile view, reading LiveKit live, was correct.
 *
 * This store bridges the gap: `LiveKitRoomConnector` publishes the active Room
 * via {@link setLiveKitRoom}, the store subscribes to the Room's media events
 * ONCE (not per-nameplate), and non-context consumers read the current value
 * with {@link getLiveKitMediaStatus} and re-render on change via
 * {@link subscribeLiveKitMediaStatus} (see `useLiveKitMediaStatus`).
 */

export interface LiveKitMediaStatus {
  micEnabled: boolean
  cameraEnabled: boolean
  /**
   * The camera is wanted on but held for its background (SC-158, F8): shown as
   * "waiting" rather than "off". Never with a live camera, which always wins.
   */
  cameraWaiting: boolean
}

let currentRoom: Room | null = null

/**
 * Remote participants who said their camera is waiting for its background,
 * keyed by the identity LiveKit gave the message — never one in the payload.
 * Per Room: a new Room starts empty.
 */
const waitingIdentities = new Set<string>()

/**
 * How long someone's "no longer waiting" may run ahead of their camera's
 * unmute before we believe it. The sender says it first, then resumes the
 * camera, and the two travel apart (data channel vs signal connection), so
 * either can arrive first. Held as waiting until the unmute, the tile goes
 * straight from its waiting cover to the video it keeps covered until painted;
 * let go at once, it showed "Camera off" in between and then a black video.
 * About the tile's paint cap (`COVER_PAINT_CAP_MS` in `CameraWaitingPlaceholder`):
 * a camera that has not come back by then is not about to.
 */
export const CAMERA_WAITING_RELEASE_GRACE_MS = 1_500

/** identity → the timer that ends a "no longer waiting" still waiting for its unmute. */
const releaseGraceTimers = new Map<string, ReturnType<typeof setTimeout>>()

/** Ours, from the connector: we do not receive our own messages. */
let localCameraWaiting = false

/** identity → set of change callbacks (one per mounted consumer for that identity). */
const identityListeners = new Map<string, Set<() => void>>()

/** Room events that can change a participant's mic/camera state. */
const MEDIA_EVENTS = [
  RoomEvent.TrackMuted,
  RoomEvent.TrackUnmuted,
  RoomEvent.TrackPublished,
  RoomEvent.TrackUnpublished,
  RoomEvent.LocalTrackPublished,
  RoomEvent.LocalTrackUnpublished,
  RoomEvent.ParticipantConnected,
  RoomEvent.ParticipantDisconnected,
] as const

function notifyIdentity(identity: string): void {
  const listeners = identityListeners.get(identity)
  if (!listeners) return
  listeners.forEach((cb) => {
    try {
      cb()
    } catch (err) {
      // One consumer's error must not stop the others from updating.
      console.error('[liveKitMediaStatusStore] listener threw', err)
    }
  })
}

function notifyAll(): void {
  identityListeners.forEach((listeners) => {
    listeners.forEach((cb) => {
      try {
        cb()
      } catch (err) {
        console.error('[liveKitMediaStatusStore] listener threw', err)
      }
    })
  })
}

function clearReleaseGrace(identity: string): void {
  const timer = releaseGraceTimers.get(identity)
  if (timer === undefined) return
  clearTimeout(timer)
  releaseGraceTimers.delete(identity)
}

function clearAllReleaseGraces(): void {
  releaseGraceTimers.forEach((timer) => clearTimeout(timer))
  releaseGraceTimers.clear()
}

/** A camera that is published but sends nothing yet: the one a "no longer waiting" is about. */
function hasMutedCamera(participant: Participant): boolean {
  return participant.getTrackPublication(Track.Source.Camera)?.isMuted === true
}

function endWaiting(identity: string): void {
  clearReleaseGrace(identity)
  waitingIdentities.delete(identity)
}

// Track events carry (publication, participant); participant events carry (participant).
const handleTrackEvent = (
  _publication: TrackPublication | RemoteTrackPublication | LocalTrackPublication,
  participant?: Participant
): void => {
  if (!participant) {
    notifyAll()
    return
  }
  // A "no longer waiting" ends on their camera coming back, or going away.
  if (releaseGraceTimers.has(participant.identity) && !hasMutedCamera(participant)) {
    endWaiting(participant.identity)
  }
  notifyIdentity(participant.identity)
}

const handleParticipantEvent = (participant: Participant): void => {
  notifyIdentity(participant.identity)
}

const handleParticipantDisconnected = (participant: Participant): void => {
  // Someone who left is not waiting for anything; if they come back they say so again.
  endWaiting(participant.identity)
  notifyIdentity(participant.identity)
}

const handleDataReceived = (
  payload: Uint8Array,
  participant?: Participant,
  _kind?: unknown,
  topic?: string
): void => {
  if (topic !== CAMERA_STATE_TOPIC || !participant) return
  const waiting = decodeCameraWaiting(payload)
  if (waiting === null) return
  const { identity } = participant
  if (waiting) {
    // Waiting again before the camera came back: still waiting, no flicker.
    clearReleaseGrace(identity)
    waitingIdentities.add(identity)
  } else if (waitingIdentities.has(identity) && hasMutedCamera(participant)) {
    // Ahead of the unmute it announces: still waiting until that lands (or the grace ends).
    if (!releaseGraceTimers.has(identity)) {
      releaseGraceTimers.set(
        identity,
        setTimeout(() => {
          releaseGraceTimers.delete(identity)
          waitingIdentities.delete(identity)
          notifyIdentity(identity)
        }, CAMERA_WAITING_RELEASE_GRACE_MS)
      )
    }
  } else {
    endWaiting(identity)
  }
  notifyIdentity(identity)
}

function attach(room: Room): void {
  room
    .on(RoomEvent.TrackMuted, handleTrackEvent)
    .on(RoomEvent.TrackUnmuted, handleTrackEvent)
    .on(RoomEvent.TrackPublished, handleTrackEvent)
    .on(RoomEvent.TrackUnpublished, handleTrackEvent)
    .on(RoomEvent.LocalTrackPublished, handleTrackEvent)
    .on(RoomEvent.LocalTrackUnpublished, handleTrackEvent)
    .on(RoomEvent.ParticipantConnected, handleParticipantEvent)
    .on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
    .on(RoomEvent.DataReceived, handleDataReceived)
}

function detach(room: Room): void {
  MEDIA_EVENTS.forEach((evt) => {
    // Both handlers are removed for whichever event they were bound to; off() is a no-op otherwise.
    room.off(evt, handleTrackEvent as never)
    room.off(evt, handleParticipantEvent as never)
  })
  room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
  room.off(RoomEvent.DataReceived, handleDataReceived)
}

/**
 * Publish (or clear, with `null`) the active LiveKit Room. Called by
 * `LiveKitRoomConnector` as the room connects and tears down.
 */
export function setLiveKitRoom(room: Room | null): void {
  if (currentRoom === room) return
  if (currentRoom) detach(currentRoom)
  currentRoom = room
  clearAllReleaseGraces()
  waitingIdentities.clear()
  if (currentRoom) attach(currentRoom)
  // Room identity changed → every consumer must re-read.
  notifyAll()
}

/**
 * Current live mic/camera state for `identity`, read straight from the Room.
 * Returns `null` when there is no connected room or the participant is absent,
 * so callers can distinguish "unknown" from a definite unmuted/enabled state.
 */
export function getLiveKitMediaStatus(identity: string): LiveKitMediaStatus | null {
  const participant = currentRoom?.getParticipantByIdentity(identity)
  if (!participant) return null
  const isLocal = participant === currentRoom?.localParticipant
  return {
    micEnabled: participant.isMicrophoneEnabled,
    cameraEnabled: participant.isCameraEnabled,
    // Ours is known first-hand. Theirs is what they said, unless their camera
    // is live — a "waiting" that outlived its "done" must never hide it.
    cameraWaiting: isLocal
      ? localCameraWaiting
      : waitingIdentities.has(identity) && !participant.isCameraEnabled,
  }
}

/** The local camera's waiting state, from the connector, which also tells everyone else. */
export function setLocalCameraWaiting(waiting: boolean): void {
  if (localCameraWaiting === waiting) return
  localCameraWaiting = waiting
  const identity = currentRoom?.localParticipant?.identity
  if (identity) notifyIdentity(identity)
}

/**
 * Subscribe to media-state changes for a single participant `identity`.
 * `callback` fires whenever a LiveKit event could have changed that
 * participant's mic/camera state (or when the Room itself changes). Returns an
 * unsubscribe function.
 */
export function subscribeLiveKitMediaStatus(identity: string, callback: () => void): () => void {
  let listeners = identityListeners.get(identity)
  if (!listeners) {
    listeners = new Set()
    identityListeners.set(identity, listeners)
  }
  listeners.add(callback)

  return () => {
    const set = identityListeners.get(identity)
    if (!set) return
    set.delete(callback)
    if (set.size === 0) identityListeners.delete(identity)
  }
}

/** Test-only reset of module state. */
export function __resetLiveKitMediaStatusStoreForTests(): void {
  if (currentRoom) detach(currentRoom)
  currentRoom = null
  identityListeners.clear()
  clearAllReleaseGraces()
  waitingIdentities.clear()
  localCameraWaiting = false
}
